import { ExternalLink, Eye, KeyRound, ListChecks, Pencil, Plus, Trash2, UserPlus, Video } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Field, FormSection } from '@/components/common/FormBits'
import { revealPasscode, useSaveMeetingRecord, type MeetingRecordInput } from '../../meetingFiles'
import { ONLINE_PLATFORMS, type AgendaItem, type Guest, type Meeting } from '../../types'
import { toastError } from '../../ui'

/** Yapılandırılmış tutanak (gündem → görüşülenler → karar), dış katılımcılar ve çevrimiçi toplantı bilgileri. */
export function MeetingRecord({ meeting: m, canWrite }: { meeting: Meeting; canWrite: boolean }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [passcode, setPasscode] = useState<string | null>(null)
  const o = m.online
  const empty = !m.agenda.length && !m.guests.length && !o.url && !o.code && !o.hasPasscode
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <ListChecks className="size-4 text-primary" aria-hidden />
          {t('crm.record.title')}
        </h3>
        {canWrite ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil />
            {t('common.edit')}
          </Button>
        ) : null}
      </div>
      {empty ? <p className="text-xs text-muted-foreground">{t('crm.record.empty')}</p> : null}
      {o.url || o.code || o.hasPasscode ? (
        <div className="space-y-1.5 rounded-lg border p-3 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <Video className="size-4 text-primary" aria-hidden />
            {o.platform ? t(`crm.record.platforms.${o.platform}`) : t('crm.record.online')}
          </p>
          {o.url ? (
            <a href={o.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-primary hover:underline">
              <ExternalLink className="size-3.5" aria-hidden />
              {t('crm.record.join')}
            </a>
          ) : null}
          {o.code ? (
            <p className="text-xs text-muted-foreground">
              {t('crm.record.code')}: <span className="font-mono">{o.code}</span>
            </p>
          ) : null}
          {o.hasPasscode ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <KeyRound className="size-3.5" aria-hidden />
              {passcode !== null ? (
                <span className="font-mono text-foreground">{passcode}</span>
              ) : canWrite ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
                  onClick={() => void revealPasscode(m.id).then(setPasscode).catch(toastError)}
                >
                  <Eye className="size-3.5" aria-hidden />
                  {t('crm.record.revealPasscode')}
                </button>
              ) : (
                t('crm.record.passcodeHidden')
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      {m.agenda.length ? (
        <ol className="space-y-2">
          {m.agenda.map((a, i) => (
            <li key={i} className="rounded-lg border p-3 text-sm">
              <p className="font-medium">
                {i + 1}. {a.title}
              </p>
              {a.notes ? <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{a.notes}</p> : null}
              {a.decision ? (
                <p className="mt-1.5 rounded-md bg-success/10 px-2 py-1 text-xs">
                  <span className="font-semibold">{t('crm.record.decision')}:</span> {a.decision}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {m.guests.length ? (
        <p className="text-sm">
          <span className="font-medium">{t('crm.record.guests')}:</span>{' '}
          <span className="text-muted-foreground">
            {m.guests.map((g) => [g.name, g.company && `(${g.company})`].filter(Boolean).join(' ')).join(', ')}
          </span>
        </p>
      ) : null}
      {editing ? <RecordModal meeting={m} onClose={() => setEditing(false)} /> : null}
    </section>
  )
}

const blankAgenda = (): AgendaItem => ({ title: '', notes: '', decision: '' })
const blankGuest = (): Guest => ({ name: '', email: '', company: '', role: '' })

function RecordModal({ meeting: m, onClose }: { meeting: Meeting; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveMeetingRecord()
  const [agenda, setAgenda] = useState<AgendaItem[]>(m.agenda.length ? m.agenda : [blankAgenda()])
  const [guests, setGuests] = useState<Guest[]>(m.guests)
  const [online, setOnline] = useState({ platform: m.online.platform, url: m.online.url, code: m.online.code })
  // Boş bırakılırsa şifre değişmez; "Şifreyi kaldır" ile silinir.
  const [passcode, setPasscode] = useState('')
  const [clearPass, setClearPass] = useState(false)
  const urlInvalid = !!online.url.trim() && !/^https?:\/\//i.test(online.url.trim())
  const guestInvalid = guests.some((g) => (g.email || g.company || g.role) && !g.name.trim())
  const agendaInvalid = agenda.some((a) => (a.notes || a.decision) && !a.title.trim())
  const invalid = urlInvalid || guestInvalid || agendaInvalid
  const patchAgenda = (i: number, p: Partial<AgendaItem>) => setAgenda((x) => x.map((a, j) => (j === i ? { ...a, ...p } : a)))
  const patchGuest = (i: number, p: Partial<Guest>) => setGuests((x) => x.map((g, j) => (j === i ? { ...g, ...p } : g)))

  const submit = () => {
    if (invalid) return
    const input: MeetingRecordInput = {
      agenda,
      guests,
      onlinePlatform: online.platform,
      onlineUrl: online.url.trim(),
      onlineCode: online.code.trim(),
      onlinePasscode: clearPass ? '' : passcode.trim() ? passcode : undefined,
    }
    save.mutate(
      { meetingId: m.id, input },
      {
        onSuccess: () => {
          toast.success(t('crm.record.saved'))
          onClose()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<ListChecks />}
      title={t('crm.record.editTitle')}
      description={t('crm.record.editHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={invalid || save.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('crm.record.agenda')}>
          {agenda.map((a, i) => (
            <div key={i} className="space-y-2 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <span className="w-6 text-sm font-semibold tabular-nums text-muted-foreground">{i + 1}.</span>
                <Input
                  aria-label={t('crm.record.agendaTitle')}
                  placeholder={t('crm.record.agendaTitle')}
                  value={a.title}
                  maxLength={300}
                  onChange={(e) => patchAgenda(i, { title: e.target.value })}
                />
                <Button size="icon" variant="ghost" className="size-8 shrink-0" aria-label={t('common.delete')} onClick={() => setAgenda((x) => x.filter((_, j) => j !== i))}>
                  <Trash2 />
                </Button>
              </div>
              <Textarea rows={2} aria-label={t('crm.record.agendaNotes')} placeholder={t('crm.record.agendaNotes')} value={a.notes} onChange={(e) => patchAgenda(i, { notes: e.target.value })} />
              <Input aria-label={t('crm.record.decision')} placeholder={t('crm.record.decision')} value={a.decision} onChange={(e) => patchAgenda(i, { decision: e.target.value })} />
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setAgenda((x) => [...x, blankAgenda()])}>
            <Plus />
            {t('crm.record.addAgenda')}
          </Button>
          {agendaInvalid ? <p className="text-xs text-destructive">{t('crm.record.agendaInvalid')}</p> : null}
        </FormSection>

        <FormSection title={t('crm.record.guests')}>
          <p className="text-xs text-muted-foreground">{t('crm.record.guestsHint')}</p>
          {guests.map((g, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <Input aria-label={t('crm.record.guestName')} placeholder={t('crm.record.guestName')} value={g.name} onChange={(e) => patchGuest(i, { name: e.target.value })} />
              <Input type="email" aria-label={t('crm.record.guestEmail')} placeholder={t('crm.record.guestEmail')} value={g.email} onChange={(e) => patchGuest(i, { email: e.target.value })} />
              <Input aria-label={t('crm.record.guestCompany')} placeholder={t('crm.record.guestCompany')} value={g.company} onChange={(e) => patchGuest(i, { company: e.target.value })} />
              <Input aria-label={t('crm.record.guestRole')} placeholder={t('crm.record.guestRole')} value={g.role} onChange={(e) => patchGuest(i, { role: e.target.value })} />
              <Button size="icon" variant="ghost" className="size-9" aria-label={t('common.delete')} onClick={() => setGuests((x) => x.filter((_, j) => j !== i))}>
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setGuests((x) => [...x, blankGuest()])}>
            <UserPlus />
            {t('crm.record.addGuest')}
          </Button>
          {guestInvalid ? <p className="text-xs text-destructive">{t('crm.record.guestInvalid')}</p> : null}
        </FormSection>

        <FormSection title={t('crm.record.online')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="mr-platform" label={t('crm.record.platform')}>
              <Select
                id="mr-platform"
                value={online.platform}
                onChange={(v) => setOnline((x) => ({ ...x, platform: v as typeof x.platform }))}
                options={[{ value: '', label: t('crm.record.noPlatform') }, ...ONLINE_PLATFORMS.map((p) => ({ value: p, label: t(`crm.record.platforms.${p}`) }))]}
              />
            </Field>
            <Field id="mr-url" label={t('crm.record.url')} error={urlInvalid ? t('crm.record.urlInvalid') : undefined}>
              <Input id="mr-url" type="url" value={online.url} placeholder="https://" onChange={(e) => setOnline((x) => ({ ...x, url: e.target.value }))} />
            </Field>
            <Field id="mr-code" label={t('crm.record.code')}>
              <Input id="mr-code" value={online.code} maxLength={120} onChange={(e) => setOnline((x) => ({ ...x, code: e.target.value }))} />
            </Field>
            <Field id="mr-pass" label={t('crm.record.passcode')} hint={m.online.hasPasscode ? t('crm.record.passcodeKeep') : t('crm.record.passcodeHint')}>
              <div className="flex gap-2">
                <Input
                  id="mr-pass"
                  type="password"
                  autoComplete="new-password"
                  value={passcode}
                  maxLength={200}
                  disabled={clearPass}
                  placeholder={m.online.hasPasscode && !clearPass ? '••••••' : ''}
                  onChange={(e) => setPasscode(e.target.value)}
                />
                {m.online.hasPasscode ? (
                  <Button variant="outline" onClick={() => setClearPass((v) => !v)}>
                    {clearPass ? t('crm.record.keepPasscode') : t('crm.record.clearPasscode')}
                  </Button>
                ) : null}
              </div>
            </Field>
          </div>
        </FormSection>
      </div>
    </Modal>
  )
}
